<template>
  <div
    class="col-span-4 datav-border rounded-lg p-4 datav-gradient overflow-auto"
    style="
      height: 100%;
      display: flex;
      flex-direction: column;
      overflow-y: auto;
    "
  >
    <h2 class="text-lg font-semibold mb-4 flex items-center gap-2">
      <i class="fa fa-bar-chart text-primary"></i>补点机房
    </h2>

    <!-- 设备卡片 -->
    <div class="device-card" v-for="deviceId in devices" :key="deviceId">
      <div class="card-header">
        <h2 class="device-title">{{ getDeviceFrequency(deviceId) }}</h2>

        <div class="control-panel">
          <button class="btn btn-primary" @click="toggleMQTT(deviceId)">
            {{ getConnectButtonText(deviceId) }}
          </button>
          <button class="btn btn-success" @click="startPlayback(deviceId)" :disabled="!canPlay(deviceId)">
            播放
          </button>
          <button class="btn btn-danger" @click="stopPlayback(deviceId)" :disabled="!canStop(deviceId)">
            停止
          </button>
        </div>
        
        <span :class="['status-badge', uiState[deviceId].statusClass]">
          {{ uiState[deviceId].statusText }}
        </span>
      </div>

      <!-- 可视化区域 -->
      <div class="visualization-container">
        <div class="waveform-wrapper">
          <canvas :id="'waveform-' + deviceId"></canvas>
        </div>
        <div class="volume-wrapper">
          <div class="volume-bar-container" :id="'volume-' + deviceId"></div>
        </div>
      </div>

      <div class="stats-panel" v-if="uiState[deviceId].statusClass === 'status-playing'">
        <div class="stat-item">
          <span class="stat-label">接收数据包</span>
          <span class="stat-value">{{ getDeviceState(deviceId).packetCount }}</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">音频缓冲</span>
          <span class="stat-value">{{ getBufferTime(deviceId) }} ms</span>
        </div>
        <div class="stat-item">
          <span class="stat-label">采样率</span>
          <span class="stat-value">{{ AUDIO_CONFIG.sampleRate }} Hz</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Chart, registerables } from "chart.js";
const mqtt = require("mqtt/mqtt");

Chart.register(...registerables);

const MQTT_CONFIG = {
  host: "47.95.70.79",
  port: 8083,
  clientIdPrefix: "web-client-",
};

const AUDIO_CONFIG = {
  sampleRate: 8000,
  bufferLength: 4000,
  voltageMin: 0,
  voltageMax: 3.3,
};

export default {
  name: "Budian",
  data() {
    return {
      devices: ["adc001", "adc002", "adc003"],
      AUDIO_CONFIG,
      // 响应式 UI 状态，用于模板显示
      uiState: {
        adc001: { statusClass: 'status-disconnected', statusText: '未连接' },
        adc002: { statusClass: 'status-disconnected', statusText: '未连接' },
        adc003: { statusClass: 'status-disconnected', statusText: '未连接' }
      }
    };
  },
  
  created() {
    // 非响应式存储，避免响应式系统开销
    this.deviceState = Object.create(null);
  },
  mounted() {
    this.initAllDevices();
    // 延迟初始化图表，确保 DOM 元素完全渲染
    this.$nextTick(() => {
      setTimeout(() => {
        this.initCharts();
        // 自动连接到 MQTT 服务器并订阅主题
        this.devices.forEach((deviceId) => {
          this.connectMQTT(deviceId);
        });
      }, 100);
    });
  },
  beforeDestroy() {
    this.cleanupAllDevices();
  },
  methods: {
    initAllDevices() {
      this.devices.forEach((deviceId) => {
        this.initDevice(deviceId);
      });
    },

    initDevice(deviceId) {
      this.$set(this.deviceState, deviceId, {
        mqttClient: null,
        isMqttConnected: false,
        isPlaying: false,
        audioContext: null,
        scriptProcessor: null,
        audioBufferQueue: [],
        packetCount: 0,
        waveformChart: null,
        volumeBars: [],
        lastVolumeData: [],
      });
    },
    
    // 延迟初始化图表，确保 DOM 元素完全渲染
    initCharts() {
      this.devices.forEach((deviceId) => {
        this.createVolumeBars(deviceId);
        this.initWaveformChart(deviceId);
      });
    },

    createVolumeBars(deviceId) {
      const container = document.getElementById('volume-' + deviceId);
      if (!container) {
        console.warn(`Volume container not found for device: ${deviceId}`);
        return;
      }

      const barCount = 2;
      const bars = [];
      
      // 动态获取容器高度，确保适配不同尺寸
      const containerHeight = container.clientHeight || 100;
      
      // 使用文档片段优化DOM操作
      const fragment = document.createDocumentFragment();
      
      // 预定义样式，减少重复代码
      const createElementWithStyles = (tag, styles) => {
        const element = document.createElement(tag);
        Object.assign(element.style, styles);
        return element;
      };

      for (let i = 0; i < barCount; i++) {
        // 创建音量柱主元素
        const bar = createElementWithStyles('div', {
          position: 'relative',
          flex: '1',
          width: '16px',
          height: '1px',
          transition: 'height 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
          minHeight: '1px',
          overflow: 'hidden',
          background: 'transparent',
        });
        
        bar.className = 'volume-bar';
        bar._lastHeight = 0; // 初始化上次高度值
        bar._containerHeight = containerHeight; // 存储容器高度
        bar._currentHeight = 0; // 当前高度，用于动画
        bar._targetHeight = null; // 目标高度，用于动画
        
        // 创建渐变背景
        const gradientBg = createElementWithStyles('div', {
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          height: '100%',
          background: `linear-gradient(to top, rgba(34, 197, 94, 0.9) 0%, rgba(34, 197, 94, 0.9) ${containerHeight * 0.66}px, rgba(245, 158, 11, 0.9) ${containerHeight * 0.85}px, rgba(239, 68, 68, 0.9) ${containerHeight}px)`,
          boxShadow: '0 0 8px rgba(34, 197, 94, 0.4)',
          transition: 'box-shadow 0.2s ease'
        });
        
        // 创建网格效果
        const gridBg = createElementWithStyles('div', {
          position: 'absolute',
          bottom: '0',
          left: '0',
          right: '0',
          height: '100%',
          backgroundImage: 'linear-gradient(to top, rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(to top, rgba(255, 255, 255, 0.05) 1px, transparent 1px)',
          backgroundSize: '100% 5%, 100% 10%',
          pointerEvents: 'none'
        });
        
        // 组合元素
        bar.appendChild(gradientBg);
        bar.appendChild(gridBg);
        
        // 存储子元素引用，方便后续更新
        bar._gradientBg = gradientBg;
        bar._gridBg = gridBg;
        
        fragment.appendChild(bar);
        bars.push(bar);
      }

      // 一次性清空并添加所有元素，减少重排
      container.innerHTML = '';
      container.appendChild(fragment);

      this.deviceState[deviceId].volumeBars = bars;
    },

    initWaveformChart(deviceId) {
      const canvas = document.getElementById('waveform-' + deviceId);
      if (!canvas) return;

      const ctx = canvas.getContext("2d");

      // 销毁现有的 Chart 实例，避免画布重用错误
      const state = this.deviceState[deviceId];
      if (state.waveformChart) {
        state.waveformChart.destroy();
      }

      // 创建渐变背景
      const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
      gradient.addColorStop(0, 'rgba(37, 99, 235, 0.2)');
      gradient.addColorStop(0.5, 'rgba(59, 130, 246, 0.1)');
      gradient.addColorStop(1, 'rgba(96, 165, 250, 0.05)');
      
      const chart = new Chart(ctx, {
        type: "line",
        data: {
          labels: Array.from({ length: 500 }, (_, i) => i),
          datasets: [
            {
              label: "音频波形",
              data: Array(this.AUDIO_CONFIG.bufferLength).fill(0),
              borderColor: "#60a5fa",
              borderWidth: 1,
              backgroundColor: gradient,
              fill: true,
              tension: 0.2,
              pointRadius: 0,
              borderJoinStyle: 'round',
              borderCapStyle: 'round',
              segment: {
                borderColor: ctx => {
                  const value = ctx.p0.parsed.y;
                  const intensity = Math.abs(value) * 0.5 + 0.5;
                  return `rgba(96, 165, 250, ${intensity})`;
                }
              }
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          layout: {
            padding: {
              top: 0,
              right: 0,
              bottom: 40,
              left: 0
            }
          },
          scales: {
            x: {
              display: false,
              grid: { display: false },
              ticks: { display: false }
            },
            y: {
              min: -1,
              max: 1,
              display: false,
              grid: {
                display: true,
                color: 'rgba(255, 255, 255, 0.03)',
                lineWidth: 1,
                drawBorder: false,
                drawTicks: false
              },
              ticks: { display: false }
            },
          },
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false }
          },
          animation: {
            duration: 50,
            easing: 'linear'
          },
          elements: {
            line: { 
              tension: 0.2,
              borderWidth: 2
            },
            point: {
              radius: 0,
              hoverRadius: 0
            }
          },
          interaction: {
            intersect: false,
            mode: 'index'
          }
        },
      });

      this.deviceState[deviceId].waveformChart = chart;
    },

    toggleMQTT(deviceId) {
      const state = this.deviceState[deviceId];
      if (state.isMqttConnected) {
        this.disconnectMQTT(deviceId);
      } else {
        this.connectMQTT(deviceId);
      }
    },
    
    // 更新 UI 状态
    updateUIState(deviceId) {
      const state = this.deviceState[deviceId];
      if (!state) {
        this.uiState[deviceId] = {
          statusClass: 'status-disconnected',
          statusText: '未连接'
        };
        return;
      }
      
      if (state.isPlaying) {
        this.uiState[deviceId] = {
          statusClass: 'status-playing',
          statusText: '播放中'
        };
      } else if (state.isMqttConnected) {
        this.uiState[deviceId] = {
          statusClass: 'status-connected',
          statusText: '已连接'
        };
      } else {
        this.uiState[deviceId] = {
          statusClass: 'status-disconnected',
          statusText: '未连接'
        };
      }
    },

    connectMQTT(deviceId) {
      const state = this.deviceState[deviceId];
      const clientId = MQTT_CONFIG.clientIdPrefix + deviceId + "-" + Math.random().toString(16).substr(2, 8);
      
      const client = mqtt.connect(`ws://${MQTT_CONFIG.host}:${MQTT_CONFIG.port}`, {
        clientId: clientId,
        keepalive: 60,
        reconnectPeriod: 1000,
        connectTimeout: 5000,
      });

      client.on("connect", () => {
        state.isMqttConnected = true;
        state.mqttClient = client;

        const dataTopic = `esp32/${deviceId}/json_data`;
        const inferenceTopic = `esp32/${deviceId}/inference_result`;
        
        client.subscribe([dataTopic, inferenceTopic], (err) => {
          if (!err) {
            console.log(`${deviceId} 订阅成功: ${dataTopic}, ${inferenceTopic}`);
          }
        });
        
        // 更新 UI 状态
        this.updateUIState(deviceId);
      });

      client.on("message", (topic, payload) => {
        const message = payload.toString();
        this.handleMQTTMessage(deviceId, topic, message);
      });

      client.on("close", () => {
        state.isMqttConnected = false;
        state.mqttClient = null;
        this.stopPlayback(deviceId);
        
        // 更新 UI 状态
        this.updateUIState(deviceId);
      });

      client.on("error", (err) => {
        console.error(`${deviceId} MQTT错误:`, err);
        client.end();
      });
    },

    disconnectMQTT(deviceId) {
      const state = this.deviceState[deviceId];
      if (state.mqttClient) {
        state.mqttClient.end();
        state.isMqttConnected = false;
      }
    },

    handleMQTTMessage(deviceId, topic, message) {
      if (topic === `esp32/${deviceId}/json_data`) {
        try {
          const data = JSON.parse(message);
          this.processAudioData(deviceId, data);
          this.deviceState[deviceId].packetCount = data.packet_id || this.deviceState[deviceId].packetCount + 1;
          // 强制更新统计面板数据
          this.forceUpdateStats();
        } catch (e) {
          console.error(`${deviceId} 解析JSON数据失败:`, e);
        }
      } else if (topic === `esp32/${deviceId}/inference_result`) {
        try {
          const inferenceData = JSON.parse(message);
          // 验证是否为当前设备的推理结果
          if (inferenceData.device_id === deviceId) {
            // 存储推理结果
            const state = this.deviceState[deviceId];
            if (!state.inference) {
              state.inference = {};
            }
            state.inference = {
              ...state.inference,
              predClass: inferenceData.predicted_class || '--',
              confidence: inferenceData.confidence || 0,
              processTime: inferenceData.processing_time_ms ? `${inferenceData.processing_time_ms.toFixed(2)} ms` : '--',
              packetId: inferenceData.packet_id || '--',
              isLowConfidence: inferenceData.is_low_confidence || false,
              radioFrequency: inferenceData.radio_frequency || 0,
              timestamp: inferenceData.timestamp || '',
              clientId: inferenceData.client_id || ''
            };
            // 强制更新统计面板数据
            this.forceUpdateStats();
          }
        } catch (e) {
          console.error(`${deviceId} 解析推理结果失败:`, e);
        }
      }
    },

    processAudioData(deviceId, data) {
      const state = this.deviceState[deviceId];

      if (!data.voltage_data || !Array.isArray(data.voltage_data)) return;

      // 优化：减少计算复杂度，使用更高效的循环
      const audioSamples = [];
      const voltageMaxHalf = this.AUDIO_CONFIG.voltageMax / 2;
      const amplitudeFactor = 1.5; // 放大因子，增大波形幅度
      for (let i = 0; i < data.voltage_data.length; i++) {
        const voltage = data.voltage_data[i] / 100;
        const normalized = (voltage - voltageMaxHalf) / voltageMaxHalf;
        const amplified = normalized * amplitudeFactor; // 应用放大因子
        audioSamples.push(Math.max(-1, Math.min(1, amplified)));
      }

      state.lastVolumeData = audioSamples;

      // 检查音频质量推理结果（使用存储在 deviceState 中的推理结果）
      const inference = state.inference;
      const isBadQuality = inference && (inference.predClass === 'noise' || inference.predClass === 'nosignal');
      if (isBadQuality) {
        // 当音频质量为噪音或无信号时，清空波形图和音量柱
        // 清空波形图
        if (state.waveformChart) {
          // 创建全 0 的数据数组
          const emptyData = Array(this.AUDIO_CONFIG.bufferLength).fill(0);
          state.waveformChart.data.datasets[0].data = emptyData;
          state.waveformChart.update('none');
        }
        
        // 清空音量柱
        if (state.volumeBars && state.volumeBars.length > 0) {
          state.volumeBars.forEach(bar => {
            // 将音量柱高度重置为最小高度
            const minHeight = 1; // 最小高度为 1%
            bar.style.height = `${minHeight}%`;
            bar._lastHeight = minHeight;
            bar._currentHeight = minHeight;
            bar._targetHeight = null;
          });
        }
      } else {
        // 只有当音频质量正常时才更新波形图和音量柱
        // 为音量柱创建单独的更新机制，提高更新频率
        this.updateVolumeBars(deviceId, audioSamples);

        // 优化：使用 requestAnimationFrame 减少波形图的渲染频率
        if (!state.updateScheduled) {
          state.updateScheduled = true;
          requestAnimationFrame(() => {
            this.updateWaveformChart(deviceId, audioSamples);
            state.updateScheduled = false;
          });
        }
      }

      if (state.isPlaying) {
        state.audioBufferQueue.push(...audioSamples);

        // 优化：限制缓冲区大小
        if (state.audioBufferQueue.length > this.AUDIO_CONFIG.sampleRate * 2) {
          state.audioBufferQueue = state.audioBufferQueue.slice(-this.AUDIO_CONFIG.sampleRate * 2);
        }
        
        // 强制更新统计面板数据
        this.forceUpdateStats();
      }
    },

    updateWaveformChart(deviceId, samples) {
      const state = this.deviceState[deviceId];
      if (!state.waveformChart) return;

      // 优化：减少显示的数据点数量，提高性能
      const maxDisplayPoints = 500; // 减少到500个点
      const step = Math.max(1, Math.floor(samples.length / maxDisplayPoints));
      const displaySamples = [];
      
      for (let i = 0; i < samples.length; i += step) {
        displaySamples.push(samples[i]);
      }

      // 确保数组长度一致
      while (displaySamples.length < maxDisplayPoints) {
        displaySamples.push(0);
      }

      state.waveformChart.data.datasets[0].data = displaySamples;
      // 优化：使用 silent update 减少动画开销
      state.waveformChart.update('none');
    },

    updateVolumeBars(deviceId, samples) {
      const bars = this.deviceState[deviceId].volumeBars;
      if (!bars || bars.length !== 2 || !samples.length) return;

      // 优化：减少计算复杂度，不使用数组切片
      let max1 = 0;
      let max2 = 0;
      const mid = Math.floor(samples.length / 2);
      
      // 计算前半部分最大值
      for (let i = 0; i < mid; i++) {
        const val = Math.abs(samples[i]);
        if (val > max1) max1 = val;
      }
      
      // 计算后半部分最大值
      for (let i = mid; i < samples.length; i++) {
        const val = Math.abs(samples[i]);
        if (val > max2) max2 = val;
      }

      const scale = 1;
      const h1 = Math.min(100, max1 * scale * 100);
      const h2 = Math.min(100, max2 * scale * 100);

      // 更新音量柱高度和样式
      this.updateVolumeBar(bars[0], h1);
      this.updateVolumeBar(bars[1], h2);
    },
    
    // 更新单个音量柱
    updateVolumeBar(bar, heightPercent) {
      const containerHeight = bar._containerHeight || 100; // 默认为100px
      
      // 确保最小高度
      if (heightPercent < 1) {
        heightPercent = 1;
      }
      
      const actualHeight = (heightPercent / 100) * containerHeight; // 计算实际像素高度
      
      // 使用缓动算法使高度变化更加平滑
      if (!bar._targetHeight) {
        bar._targetHeight = heightPercent;
        bar._currentHeight = bar._lastHeight || 1;
      } else {
        bar._targetHeight = heightPercent;
      }
      
      // 检查是否需要动画
      if (Math.abs(bar._targetHeight - bar._currentHeight) > 0.5) {
        // 使用缓动函数
        const easeFactor = 0.12; // 缓动因子，值越小动画越慢
        bar._currentHeight += (bar._targetHeight - bar._currentHeight) * easeFactor;
        
        // 应用新高度
        bar.style.height = `${bar._currentHeight}%`;
        bar._lastHeight = bar._currentHeight;
        
        // 继续动画
        requestAnimationFrame(() => {
          this.updateVolumeBar(bar, bar._targetHeight);
        });
      } else {
        // 动画结束，直接设置目标高度
        bar.style.height = `${heightPercent}%`;
        bar._lastHeight = heightPercent;
        bar._currentHeight = heightPercent;
        bar._targetHeight = null;
      }
      
      // 根据实际像素高度更新阴影效果
      if (bar._gradientBg) {
        if (actualHeight > 85) {
          bar._gradientBg.style.boxShadow = '0 0 16px rgba(239, 68, 68, 0.7)';
        } else if (actualHeight > 70) {
          bar._gradientBg.style.boxShadow = '0 0 12px rgba(245, 158, 11, 0.6)';
        } else {
          bar._gradientBg.style.boxShadow = '0 0 8px rgba(34, 197, 94, 0.4)';
        }
      }
    },

    startPlayback(deviceId) {
      const state = this.deviceState[deviceId];

      if (state.isPlaying) return;

      if (!state.audioContext) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        state.audioContext = new AudioContext({ sampleRate: this.AUDIO_CONFIG.sampleRate });

        state.scriptProcessor = state.audioContext.createScriptProcessor(1024, 1, 1);

        state.scriptProcessor.onaudioprocess = (e) => {
          const outputBuffer = e.outputBuffer.getChannelData(0);

          for (let i = 0; i < outputBuffer.length; i++) {
            outputBuffer[i] = state.audioBufferQueue.shift() || 0;
          }
        };

        state.scriptProcessor.connect(state.audioContext.destination);
      }

      if (state.audioContext.state === "suspended") {
        state.audioContext.resume();
      }

      state.isPlaying = true;

      if (state.lastVolumeData.length > 0) {
        state.audioBufferQueue.push(...state.lastVolumeData);
      }
      
      // 更新 UI 状态
      this.updateUIState(deviceId);
    },

    stopPlayback(deviceId) {
      const state = this.deviceState[deviceId];

      state.isPlaying = false;
      state.audioBufferQueue = [];

      if (state.audioContext && state.audioContext.state === "running") {
        state.audioContext.suspend();
      }
      
      // 更新 UI 状态
      this.updateUIState(deviceId);
    },

    cleanupAllDevices() {
      this.devices.forEach((deviceId) => {
        const state = this.deviceState[deviceId];
        this.stopPlayback(deviceId);
        this.disconnectMQTT(deviceId);
        if (state.audioContext) {
          state.audioContext.close();
        }
      });
    },

    getStatusClass(deviceId) {
      const state = this.deviceState[deviceId];
      if (!state) return "status-disconnected";
      if (state.isPlaying) return "status-playing";
      if (state.isMqttConnected) return "status-connected";
      return "status-disconnected";
    },

    getStatusText(deviceId) {
      const state = this.deviceState[deviceId];
      if (!state) return "未连接";
      if (state.isPlaying) return "播放中";
      if (state.isMqttConnected) return "已连接";
      return "未连接";
    },

    getConnectButtonText(deviceId) {
      const state = this.deviceState[deviceId];
      if (!state) return "连接";
      return state.isMqttConnected ? "断开" : "连接";
    },

    canPlay(deviceId) {
      const state = this.deviceState[deviceId];
      return state && state.isMqttConnected && !state.isPlaying;
    },

    canStop(deviceId) {
      const state = this.deviceState[deviceId];
      return state && state.isPlaying;
    },

    getDeviceState(deviceId) {
      return this.deviceState[deviceId] || { packetCount: 0, audioBufferQueue: [] };
    },

    getBufferTime(deviceId) {
      const state = this.deviceState[deviceId];
      if (!state || !state.audioBufferQueue) return 0;
      const bufferMs = (state.audioBufferQueue.length / this.AUDIO_CONFIG.sampleRate) * 1000;
      return Math.round(bufferMs);
    },
    
    // 强制重新渲染组件，更新统计面板数据
    forceUpdateStats() {
      this.$forceUpdate();
    },
    
    // 获取设备对应的频率
    getDeviceFrequency(deviceId) {
      const frequencyMap = {
        adc001: '湖南新闻-91.2MHz',
        adc002: '经济之声-93.2MHz',
        adc003: '中国之声-95.2MHz'
      };
      return frequencyMap[deviceId] || '音频采集设备';
    },
  },
};
</script>

<style lang="scss" scoped>
.device-card {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-wrap: wrap;
}

.device-title {
  font-size: 16px;
  font-weight: 600;
  color: #fff;
}

.status-badge {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-disconnected {
  background: rgba(220, 38, 38, 0.2);
  color: #fca5a5;
}

.status-connected {
  background: rgba(22, 163, 74, 0.2);
  color: #86efac;
}

.status-playing {
  background: rgba(37, 99, 235, 0.2);
  color: #93c5fd;
}

.control-panel {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
  justify-content: right;
}

.btn {
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-success {
  background: #16a34a;
  color: white;
}

.btn-success:hover {
  background: #15803d;
}

.btn-danger {
  background: #dc2626;
  color: white;
}

.btn-danger:hover {
  background: #b91c1c;
}

.btn:disabled {
  background: #6b7280;
  cursor: not-allowed;
  opacity: 0.5;
}

.visualization-container {
  display: flex;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px;
  background: rgba(255, 255, 255, 0.03);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.waveform-wrapper {
  flex: 3;
  position: relative;
  height: 150px;
  background: rgba(37, 99, 235, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(37, 99, 235, 0.2);
  overflow: hidden;
}

/* 移除底部水平线，使波形图居中显示 */
/* .waveform-wrapper::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(37, 99, 235, 0.3), transparent);
} */

/* 添加中心线，帮助波形图居中显示 */
.waveform-wrapper::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(37, 99, 235, 0.3), transparent);
  z-index: 1;
}

/* 保留波形文字标签 */
.waveform-wrapper::after {
  content: '波形';
  position: absolute;
  top: 8px;
  left: 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  background: none;
  z-index: 2;
}

.volume-wrapper {
  flex: 1;
  position: relative;
  height: 150px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: rgba(37, 99, 235, 0.05);
  border-radius: 6px;
  border: 1px solid rgba(37, 99, 235, 0.2);
  padding: 6px;
  padding-top: 30px;
}

.volume-bar-container {
  display: flex;
  align-items: flex-end;
  height: 100%;
  gap: 4px;
  padding: 6px;
  // background: rgba(255, 255, 255, 0.02);
  border-radius: 6px;
  // border: 1px solid rgba(255, 255, 255, 0.05);
}

.volume-bar {
  flex: 1;
  position: relative;
  border-radius: 6px 6px 0 0;
  transition: height 0.15s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease;
  min-height: 6px;
  overflow: hidden;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: none;
}

.volume-bar::before {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  background: linear-gradient(to top, 
    rgba(34, 197, 94, 0.9) 0%,
    rgba(34, 197, 94, 0.9) 60%,
    rgba(245, 158, 11, 0.9) 80%,
    rgba(239, 68, 68, 0.9) 100%
  );
  box-shadow: 0 0 8px rgba(34, 197, 94, 0.4);
  transition: box-shadow 0.2s ease;
}

.volume-bar:hover::before {
  opacity: 0.9;
  box-shadow: 0 0 14px rgba(34, 197, 94, 0.6);
}

.volume-bar::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  background-image: 
    linear-gradient(to top, rgba(255, 255, 255, 0.1) 1px, transparent 1px),
    linear-gradient(to top, rgba(255, 255, 255, 0.05) 1px, transparent 1px);
  background-size: 100% 5%, 100% 10%;
  pointer-events: none;
}

.volume-bar.high-volume::before {
  box-shadow: 0 0 16px rgba(239, 68, 68, 0.7);
}

.volume-bar.medium-volume::before {
  box-shadow: 0 0 12px rgba(245, 158, 11, 0.6);
}

.volume-wrapper::before {
  content: '音量';
  position: absolute;
  top: 8px;
  left: 12px;
  font-size: 12px;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

/* 波形文字标签已移至 .waveform-wrapper::after 伪元素中 */

.waveform-wrapper canvas {
  width: 100% !important;
  height: 100% !important;
}

.stats-panel {
  display: flex;
  gap: 10px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.7);
  flex-wrap: wrap;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.stat-label {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.5);
}

.stat-value {
  color: #fff;
  font-weight: 600;
}

.h-24 {
  height: 6rem;
}

.max-h-40 {
  max-height: 10rem;
}
</style>
